import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from "../../basic/dialog.tsx";
import {Button} from "../../basic/button.tsx";
import {Download, Sparkles, UserPlus} from "lucide-react";
import {useTranslationWithKeys} from "@/hooks/useTranslationWithKeys.ts";
import {useAuth} from "@/hooks/useAuth.ts";

interface WelcomeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDownload: () => void;
  onInviteTeam: () => void;
}

export function WelcomeDialog({ open, onOpenChange, onDownload, onInviteTeam }: WelcomeDialogProps) {
  const {t} = useTranslationWithKeys();
  const {user} = useAuth();
  const canInviteTeam = user?.roles?.some(role => ['ADMIN', 'PLATFORM_ADMIN'].includes(role));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
          </div>
          <DialogTitle className="text-center text-2xl">
            {canInviteTeam ? t('welcome.title') : t('welcome.memberTitle')}
          </DialogTitle>
          <DialogDescription className="text-center pt-2">
            {t('welcome.subtitle')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="space-y-4">
            <p className="text-center text-muted-foreground">
              {canInviteTeam ? t('welcome.description') : t('welcome.memberDescription')}
            </p>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Download className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="mb-1">{t('welcome.downloadApp')}</p>
                  <p className="text-muted-foreground">
                    {t('welcome.downloadAppDescription')}
                  </p>
                </div>
              </div>
              
              {canInviteTeam && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <UserPlus className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="mb-1">{t('welcome.inviteTeam')}</p>
                    <p className="text-muted-foreground">
                      {t('welcome.inviteTeamDescription')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
              <Button
              className="flex-1 rounded-full"
              onClick={() => {
                onOpenChange(false);
                onDownload();
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              {t('welcome.downloadApp')}
            </Button>
            {canInviteTeam && (
              <Button
                variant="outline"
                className="flex-1 rounded-full"
                onClick={() => {
                  onOpenChange(false);
                  onInviteTeam();
                }}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                {t('welcome.inviteTeamMembers')}
              </Button>
            )}
          </div>

            <Button
                variant="ghost"
            className="w-full"
            onClick={() => onOpenChange(false)}
          >
            {t('welcome.exploreOnMyOwn')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
